import { useQuery } from "@tanstack/react-query";
import { type AxiosError } from "axios";

import { productApi } from "~/store/initializeStore";

export interface RenewalCohortData {
  cohortMonth: string; // "2025-01"
  cohortLabel: string; // "Jan 2025"
  totalCustomers: number;
  customersWithExpiredAccess: number;
  /** Repurchases and upgrades together — the two fields below split the same number. */
  customersWhoRenewed: number;
  /** Absent until the product's stats are recomputed after the split was introduced. */
  customersWhoRepurchased?: number;
  customersWhoUpgraded?: number;
  renewalRate: number; // %
  initialPackages: Record<string, number>; // keyed by priceId
  renewalPackages: Record<string, number>; // keyed by priceId
  upgradePackages?: Record<string, number>; // keyed by priceId, the upgrade subset
  avgDaysToRenewal: number | null;
  initialRevenue: number; // in cents
  renewalRevenue: number; // in cents, repurchases AND upgrades
  upgradeRevenue?: number; // in cents, the upgrade part of renewalRevenue
}

export interface RenewalCohortStatsResponse {
  lastUpdated: string;
  cohorts: RenewalCohortData[];
}

export const useProductRenewalCohortStats = (productId: string) => {
  return useQuery<RenewalCohortStatsResponse | null, AxiosError>({
    queryKey: ["productRenewalCohortStats", productId],
    enabled: Boolean(productId),
    staleTime: 5 * 60 * 1000,
    retry: (failureCount, error) => error.response?.status !== 404 && failureCount < 2,
    queryFn: async () => {
      try {
        const response = await productApi.get<RenewalCohortStatsResponse>(
          `/products/${productId}/renewal-cohort-stats`,
        );
        return response.data;
      } catch (error) {
        if ((error as AxiosError).response?.status === 404) {
          return null; // stats not generated yet
        }
        throw error;
      }
    },
  });
};
